import React, { useState } from 'react'
import PropTypes from 'prop-types'
import connect from 'react-redux/es/connect/connect'
import { useDispatch } from 'react-redux'
import styled from 'styled-components/macro'
import ProjectSearchResults from 'components/ProjectSearchResults'
import ProgrammeSearchResults from 'components/ProgrammeSearchResults'
import PartnerSearchResults from 'components/PartnerSearchResults'
import CountriesAndRegionsResults from 'components/CountriesAndRegionsResults'
import breakpoints from 'assets/stylesheet'
import { SearchableOptions } from 'store/constants/constants'
import {
  applyDocumentSearch,
  applyHeatMapSearch,
  applySearch,
  setMapDownloadLoading
} from 'store/actions/search'
import pako from 'pako'
import { setResultHash } from 'store/actions/filters'
import * as htmlToImage from 'html-to-image'
import { toast } from 'react-toastify'
import { t } from 'ttag'
import getBackendURL from 'util/backend-url'

const ResultsContainer = styled.div`
  padding: ${props =>
    props.isSidebarVisible ? '20px' : !props.reduced ? '15px' : '15px 15px 15px 70px'};
  width: ${props =>
    props.isSidebarVisible ? 'calc(100% - 430px)' : props.reduced ? '100%' : 'calc(100% - 430px)'};
  position: relative;
  z-index: -2;
  transition: all 0.4s ease;
  flex: ${props => (props.isSidebarVisible ? 'auto' : 'none')};
  left: 0;
  @media (max-width: ${breakpoints.bpTabletPx}) {
    width: 100%;
    left: 0;
    display: ${props => (props.isSidebarVisible ? 'none' : 'block')};
    padding: 15px;
    position: ${props => (!props.reduced ? 'absolute' : 'static')};
    right: ${props => (!props.reduced ? '-100%' : '0')};
    left: ${props => (!props.reduced ? 'unset' : '0')};
  }
  @media (max-width: ${breakpoints.bpPhonePx}) {
    padding: 15px;
  }
`

function ResultsComponent({
  search: {
    isPending,
    searchResults,
    documentSearchResults,
    error,
    isMapDownloadPending,
    previousSearch,
    previousDocumentSearch
  },
  orderby,
  resetOrder,
  currentSearchable,
  isSidebarVisible,
  reduced,
  location: { hasPermission, hasResults },
  currentFilters,
  selectedFilters,
  resultHash
}) {
  const dispatch = useDispatch()
  const [isExcelExportPending, setIsExcelExportPending] = useState(false)
  const [isExcelDocumentPending, setisExcelDocumentPending] = useState(false)
  const [state, setState] = useState('setState')
  const moveToTop = () => {
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0
  }
  const handleDocumentSearch = page => {
    dispatch(
      applyDocumentSearch({
        ...previousDocumentSearch,
        page
      })
    )
  }

  const applySearchFilters = (page, orderby) => {
    const data = {
      currentSearchable,
      page,
      orderby,
      body: previousSearch
    }
    dispatch(applySearch(data)).then(response => {
      if (currentSearchable === SearchableOptions.PROJECTS) {
        if (!error) {
          const foundProjectIds = response.value ? response.value.project_ids : []
          if (foundProjectIds.length > 0) {
            dispatch(
              applyHeatMapSearch({
                body: {
                  ...previousSearch,
                  all_partnerships: true
                }
              })
            )
            handleDocumentSearch(1)
          }
        }
      }
    })
  }
  const handlePageChange = (page, isDocumentPage = false, orderby) => {
    moveToTop()
    if (isDocumentPage) {
      handleDocumentSearch(page)
    } else {
      applySearchFilters(page, orderby)
    }
  }

  const handleOrderChange = (orderby, isDocumentPage = false) => {
    resetOrder(orderby)
    moveToTop()
    if (isDocumentPage) {
      handleDocumentSearch(1)
    } else {
      applySearchFilters(1, orderby)
    }
    setState({
      orderby: orderby
    })
  }

  const parseXhrFilename = (xhr, fallback) => {
    const disposition = xhr.getResponseHeader('Content-Disposition')
    if (disposition) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      const matches = filenameRegex.exec(disposition)
      if (matches != null && matches[1]) {
        return matches[1].replace(/['"]/g, '')
      }
    }
    return typeof fallback === 'undefined' ? 'search_results_keep_eu.xlsx' : fallback
  }

  const handleExportToExcel = (actionData, isProjectDocument = false, type) => {
    const body = {
      ...selectedFilters[currentSearchable],
      response_type: 'excel'
    }

    type !== 'doc' ? setIsExcelExportPending(true) : null
    type === 'doc' ? setisExcelDocumentPending(true) : null
    const apiUrl = getBackendURL()
    const url = `${apiUrl}/api/search/${currentSearchable}/`
    const documentUrl = `${apiUrl}/api/search/project-attachments/`
    const xhr = new XMLHttpRequest()

    xhr.onreadystatechange = () => {
      let a
      if (xhr.readyState === 4 && xhr.status === 200) {
        // Trick for making downloadable link
        a = document.createElement('a')
        a.href = window.URL.createObjectURL(xhr.response)
        // Give filename you wish to download
        a.download = parseXhrFilename(xhr)
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        setIsExcelExportPending(false)
        setisExcelDocumentPending(false)
      }
    }

    xhr.onerror = () => {
      toast.error(t`An error occurred when trying to download the file`)
      setIsExcelExportPending(false)
      setisExcelDocumentPending(false)
    }

    if (isProjectDocument) {
      const documentBody = {
        ...selectedFilters[currentSearchable],
        ...previousDocumentSearch.body,
        response_type: 'excel'
      }

      xhr.open('POST', documentUrl)
      xhr.setRequestHeader('Content-Type', 'application/json')
      xhr.responseType = 'blob'
      xhr.send(JSON.stringify(documentBody))

      return
    }

    xhr.open('POST', url)
    xhr.setRequestHeader('Content-Type', 'application/json')
    xhr.responseType = 'blob'
    xhr.send(JSON.stringify(body))
  }

  const handleGetResultUrl = () => {
    const binaryString = btoa(
      pako.deflate(JSON.stringify(selectedFilters[currentSearchable]), {
        to: 'string'
      })
    )
    dispatch(setResultHash(binaryString))
  }

  const handleMapImageDownload = () => {
    dispatch(
      setMapDownloadLoading({
        isLoading: true
      })
    )
    const map = document.getElementById('results-map')
    htmlToImage.toPng(map).then(dataUrl => {
      const link = document.createElement('a')
      link.download = 'map.png'
      link.href = dataUrl
      link.click()
      dispatch(
        setMapDownloadLoading({
          isLoading: false
        })
      )
    })
  }
  let resultComponent
  if (currentSearchable === SearchableOptions.PROJECTS) {
    resultComponent = (
      <ProjectSearchResults
        searchResults={searchResults}
        documentSearchResults={documentSearchResults}
        isPending={isPending}
        error={error}
        orderby={orderby ? orderby : state.orderby}
        orderChanged={i => handleOrderChange(i)}
        isSidebarVisible={isSidebarVisible}
        reduced={reduced}
        onPageChange={handlePageChange}
        hasLocationPermission={hasPermission}
        hasLocationSearchResults={hasResults}
        onExportToExcel={handleExportToExcel}
        currentFilters={currentFilters}
        onGetResultUrl={handleGetResultUrl}
        resultHash={resultHash}
        onMapImageDownload={handleMapImageDownload}
        isMapDownloadPending={isMapDownloadPending}
        isExcelExportPending={isExcelExportPending}
        isExcelDocumentPending={isExcelDocumentPending}
        selectedFilters={selectedFilters}
      />
    )
  } else if (currentSearchable === SearchableOptions.PROGRAMMES) {
    resultComponent = (
      <ProgrammeSearchResults
        searchResults={searchResults}
        isPending={isPending}
        error={error}
        orderby={orderby ? 'recent' : state.orderby}
        orderChanged={i => handleOrderChange(i)}
        isSidebarVisible={isSidebarVisible}
        reduced={reduced}
        onPageChange={handlePageChange}
        onExportToExcel={handleExportToExcel}
        currentFilters={currentFilters}
        onGetResultUrl={handleGetResultUrl}
        resultHash={resultHash}
        isExcelExportPending={isExcelExportPending}
        selectedFilters={selectedFilters}
      />
    )
  } else if (currentSearchable === SearchableOptions.PARTNERS) {
    resultComponent = (
      <PartnerSearchResults
        searchResults={searchResults}
        isPending={isPending}
        error={error}
        isSidebarVisible={isSidebarVisible}
        reduced={reduced}
        onPageChange={handlePageChange}
        onExportToExcel={handleExportToExcel}
        currentFilters={currentFilters}
        onGetResultUrl={handleGetResultUrl}
        resultHash={resultHash}
        isExcelExportPending={isExcelExportPending}
      />
    )
  } else if (currentSearchable === SearchableOptions.COUNTRIES_AND_REGIONS) {
    resultComponent = (
      <CountriesAndRegionsResults
        searchResults={searchResults}
        isPending={isPending}
        error={error}
        isSidebarVisible={isSidebarVisible}
        reduced={reduced}
        onPageChange={handlePageChange}
        currentFilters={currentFilters}
      />
    )
  }

  return (
    <ResultsContainer isSidebarVisible={isSidebarVisible} reduced={reduced}>
      {resultComponent}
    </ResultsContainer>
  )
}

ResultsComponent.propTypes = {
  search: PropTypes.shape({
    isPending: PropTypes.bool,
    searchResults: PropTypes.object,
    documentSearchResults: PropTypes.object,
    error: PropTypes.bool,
    isMapDownloadPending: PropTypes.bool,
    previousSearch: PropTypes.object,
    previousDocumentSearch: PropTypes.object
  }),
  orderby: PropTypes.string,
  reduced: PropTypes.bool,
  resetOrder: PropTypes.func,
  currentSearchable: PropTypes.string,
  isSidebarVisible: PropTypes.bool,
  location: PropTypes.shape({ hasPermission: PropTypes.bool, hasResults: PropTypes.bool }),
  currentFilters: PropTypes.array,
  selectedFilters: PropTypes.object,
  resultHash: PropTypes.string
}

const mapStateToProps = ({ search }) => ({
  search
})

export default connect(mapStateToProps)(ResultsComponent)
